import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CeldaVigencia, ProgressBar, StatusBadge, VigenciaModal } from "@/components/ds";
import { AlertCircle, Loader2 } from "@/components/ds/icons";
import { toast } from "sonner";
import { CabeceraGrupoDocumentos } from "@/components/documentos/CabeceraGrupoDocumentos";
import { DocumentoFilaAcciones } from "@/components/documentos/DocumentoFilaAcciones";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { apiRecursoDocumentosAdapter } from "@/lib/recurso-documentos-adapter";
import { estadoVencimiento, type EstadoVencimiento } from "@/lib/vencimiento";
import type { RecursoDocumentoTipo } from "@/types/recurso-documento-tipo";
import type { RecursoDocumento, RecursoAmbito } from "@/types/recurso-documento";

// Sin selección de adapter porque no existe una implementación mock para
// `recurso_documentos`: el backend ya estaba completo (B1→B4) cuando se
// escribió.
const adapter = apiRecursoDocumentosAdapter;

function mensajeDeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message;
  return fallback;
}

function esCancelacion(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** `uploaded_at` viene con hora (ISO completo), así que `new Date` alcanza. */
function formatFechaSubida(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Grilla de las filas de "Con documento cargado". Envuelve a una columna bajo
 * `sm` en vez de comprimirse (regla de grillas del DS).
 *
 * La columna de vigencia lleva DOS líneas ("21 jul 2026" + "en 44 días · sin
 * avisos"): con el tope viejo de 148px la segunda se desbordaba por encima de
 * los botones de acción, y no lo delataba `scrollWidth` —un hijo de un flex en
 * columna con `items-start` crece sin generar scroll— sino la rasterización. */
const GRID_CARGADO = "sm:grid-cols-[minmax(180px,1fr)_112px_minmax(150px,190px)_112px]";
/** Ídem para "Sin documento cargado": nombre + acción. */
const GRID_SIN_CARGAR = "sm:grid-cols-[minmax(180px,1fr)_auto]";

/** Lo urgente primero: vencido, después por vencer, después el resto. */
function ordenPorUrgencia(vigencia: EstadoVencimiento): number {
  if (vigencia === "vencido") return 0;
  if (vigencia === "por_vencer") return 1;
  return 2;
}

interface RecursoDocumentosPanelProps {
  ambito: RecursoAmbito;
  documentableId: number;
}

interface Fila {
  tipo: RecursoDocumentoTipo;
  documento: RecursoDocumento;
  vigencia: EstadoVencimiento;
}

/**
 * Documentos de un recurso (EPP / vehículo / maquinaria), separados en dos
 * grupos: lo que falta subir primero, lo que ya está después.
 *
 * Es el mismo molde que `DocumentacionGeneralPanel` (Documentación General del
 * trabajador) y comparte con él la cabecera de grupo y la botonera de fila. La
 * diferencia con aquel es de dominio y no de forma: acá el catálogo es CERRADO
 * (máximo 6 tipos por ámbito) y no hay "exigibles" que configurar, así que el
 * denominador es el catálogo entero y no hace falta buscador.
 */
export function RecursoDocumentosPanel({ ambito, documentableId }: RecursoDocumentosPanelProps) {
  const [tipos, setTipos] = useState<RecursoDocumentoTipo[]>([]);
  const [documentos, setDocumentos] = useState<RecursoDocumento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyTipoId, setBusyTipoId] = useState<number | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Fila | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Subida inline: `Subir`/`Reemplazar` disparan el picker del SO directo
  // (sin modal) — este estado alimenta la barra de progreso + Cancelar
  // mientras la subida está en curso. Se indexa por `tipoDocumentoId` porque
  // puede haber a lo más una subida por slot.
  const [uploadingPercents, setUploadingPercents] = useState<Record<number, number>>({});
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());

  const [vencimientoTarget, setVencimientoTarget] = useState<Fila | null>(null);

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [tiposRes, documentosRes] = await Promise.all([
        adapter.listTipos(ambito),
        adapter.listDocumentos(ambito, documentableId),
      ]);
      setTipos(tiposRes);
      setDocumentos(documentosRes);
    } catch (err) {
      setLoadError(mensajeDeError(err, "No se pudo cargar la documentación."));
    } finally {
      setIsLoading(false);
    }
  }, [ambito, documentableId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Al desmontar (ej. cambiar de tab o salir de la ficha) abortamos cualquier
  // subida en curso: evita subidas colgadas y setState sobre un componente
  // desmontado.
  useEffect(
    () => () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
    },
    [],
  );

  const documentoPorTipo = useMemo(() => {
    const map = new Map<number, RecursoDocumento>();
    for (const doc of documentos) map.set(doc.tipo_documento_id, doc);
    return map;
  }, [documentos]);

  const { conDocumento, sinDocumento, resumen } = useMemo(() => {
    const con: Fila[] = [];
    const sin: RecursoDocumentoTipo[] = [];

    for (const tipo of tipos) {
      const documento = documentoPorTipo.get(tipo.id);
      if (!documento) {
        sin.push(tipo);
        continue;
      }
      con.push({ tipo, documento, vigencia: estadoVencimiento(documento.fecha_vencimiento) });
    }

    con.sort(
      (a, b) =>
        ordenPorUrgencia(a.vigencia) - ordenPorUrgencia(b.vigencia) ||
        a.tipo.orden - b.tipo.orden,
    );

    const vencidos = con.filter((f) => f.vigencia === "vencido").length;
    const porVencer = con.filter((f) => f.vigencia === "por_vencer").length;

    return {
      conDocumento: con,
      sinDocumento: sin,
      resumen: {
        total: tipos.length,
        // "Vigentes" = cargados que todavía valen. Un documento por vencer
        // SIGUE valiendo, así que cuenta; uno vencido no. Es el mismo criterio
        // que el numerador de la columna Documentos del listado.
        vigentes: con.length - vencidos,
        faltantes: sin.length,
        vencidos,
        porVencer,
      },
    };
  }, [tipos, documentoPorTipo]);

  const resumenTexto = useMemo(() => {
    const partes = [`${resumen.vigentes} de ${resumen.total} documentos vigentes`];
    if (resumen.faltantes > 0) partes.push(`${resumen.faltantes} sin subir`);
    if (resumen.vencidos > 0) {
      partes.push(`${resumen.vencidos} ${resumen.vencidos === 1 ? "vencido" : "vencidos"}`);
    }
    if (resumen.porVencer > 0) partes.push(`${resumen.porVencer} por vencer`);
    return partes.join(" · ");
  }, [resumen]);

  async function handlePickFile(tipo: RecursoDocumentoTipo, file: File) {
    const controller = new AbortController();
    abortControllersRef.current.set(tipo.id, controller);
    setUploadingPercents((prev) => ({ ...prev, [tipo.id]: 0 }));
    try {
      const documento = await adapter.upload(ambito, documentableId, tipo.id, file, {
        onProgress: (percent) => setUploadingPercents((prev) => ({ ...prev, [tipo.id]: percent })),
        signal: controller.signal,
      });
      setDocumentos((prev) => [...prev.filter((d) => d.tipo_documento_id !== tipo.id), documento]);
      toast.success(`${tipo.label} subido correctamente.`);
    } catch (err) {
      // Cancelación del usuario (botón "Cancelar") — no es un error, no se notifica.
      if (!esCancelacion(err)) {
        toast.error(mensajeDeError(err, "No se pudo subir el documento."));
      }
    } finally {
      abortControllersRef.current.delete(tipo.id);
      setUploadingPercents((prev) => {
        const next = { ...prev };
        delete next[tipo.id];
        return next;
      });
    }
  }

  function handleCancelUpload(tipoId: number) {
    abortControllersRef.current.get(tipoId)?.abort();
  }

  async function handleVencimientoSubmit(opts: {
    fechaVencimiento: string | null;
    notificarDias: number[];
  }) {
    if (!vencimientoTarget) return;
    const { tipo, documento } = vencimientoTarget;
    setBusyTipoId(tipo.id);
    try {
      const updated = await adapter.updateVencimiento(ambito, documentableId, documento.id, opts);
      setDocumentos((prev) => [...prev.filter((d) => d.id !== documento.id), updated]);
      toast.success(`Vencimiento de ${tipo.label} actualizado.`);
    } finally {
      setBusyTipoId(null);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    const { tipo, documento } = deleteTarget;
    setIsDeleting(true);
    try {
      await adapter.destroy(ambito, documentableId, documento.id);
      setDocumentos((prev) => prev.filter((d) => d.id !== documento.id));
      toast.success(`${tipo.label} eliminado.`);
      setDeleteTarget(null);
    } catch {
      toast.error("No se pudo eliminar el documento.");
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleDownload(tipo: RecursoDocumentoTipo, documento: RecursoDocumento) {
    setBusyTipoId(tipo.id);
    try {
      await adapter.download(ambito, documentableId, documento);
    } catch {
      toast.error("No se pudo descargar el documento.");
    } finally {
      setBusyTipoId(null);
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 pt-6">
          <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
          <AlertCircle className="text-destructive h-6 w-6" />
          <p className="text-muted-foreground text-sm">{loadError}</p>
          <Button variant="outline" size="sm" onClick={cargar}>
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (tipos.length === 0) {
    return (
      <Card>
        <CardContent className="text-muted-foreground py-12 pt-6 text-center text-sm">
          No hay tipos de documento configurados todavía.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Resumen: la misma cuenta que muestra la columna Documentos del
          listado, para que la ficha y la tabla nunca digan cosas distintas. */}
      <section className="bg-card flex flex-col gap-2 rounded-xl border px-4 py-3.5">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="fc-section-title">Documentos del equipo</span>
          <span className="text-muted-foreground text-xs">{resumenTexto}</span>
        </div>
        <ProgressBar
          valor={resumen.vigentes}
          total={resumen.total}
          alto={7}
          className="max-w-[420px]"
        />
      </section>

      {sinDocumento.length > 0 && (
        <section className="bg-card overflow-hidden rounded-xl border">
          <CabeceraGrupoDocumentos
            icono="pending_actions"
            titulo="Sin documento cargado"
            cuenta={sinDocumento.length}
            tono="warning"
          />
          {sinDocumento.map((tipo) => (
            <div
              key={tipo.id}
              className={cn(
                "border-border-faint grid gap-2 border-b px-4 py-2.5 last:border-b-0",
                "hover:bg-primary-50 transition-colors sm:items-center sm:gap-3",
                GRID_SIN_CARGAR,
              )}
            >
              <span className="fc-cell fc-truncate">{tipo.label}</span>
              <DocumentoFilaAcciones
                label={tipo.label}
                tieneDocumento={false}
                busy={busyTipoId === tipo.id}
                uploading={tipo.id in uploadingPercents}
                uploadPercent={uploadingPercents[tipo.id] ?? 0}
                onPickFile={(file) => handlePickFile(tipo, file)}
                onCancelUpload={() => handleCancelUpload(tipo.id)}
                onDownload={() => {}}
                onRequestDelete={() => {}}
              />
            </div>
          ))}
        </section>
      )}

      {conDocumento.length > 0 && (
        <section className="bg-card overflow-hidden rounded-xl border">
          <CabeceraGrupoDocumentos
            icono="task"
            titulo="Con documento cargado"
            cuenta={conDocumento.length}
            tono="neutro"
          />
          {conDocumento.map((fila) => {
            const { tipo, documento, vigencia } = fila;
            const uploading = tipo.id in uploadingPercents;
            return (
              <div
                key={tipo.id}
                className={cn(
                  "border-border-faint grid gap-2 border-b px-4 py-2.5 last:border-b-0",
                  "hover:bg-primary-50 transition-colors sm:items-center sm:gap-3",
                  GRID_CARGADO,
                )}
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="fc-cell fc-truncate">{tipo.label}</span>
                  <span className="fc-meta fc-truncate">
                    {formatBytes(documento.size_bytes)} · subido{" "}
                    {formatFechaSubida(documento.uploaded_at)}
                  </span>
                </div>

                <div className="flex min-w-0 items-center">
                  {uploading ? (
                    <div className="w-full max-w-[100px]">
                      <Progress value={uploadingPercents[tipo.id] ?? 0} className="h-1.5" />
                    </div>
                  ) : (
                    <StatusBadge estado={vigencia === "vencido" ? "vencido" : vigencia === "por_vencer" ? "por_vencer" : "subido"}>
                      {vigencia === "vencido" || vigencia === "por_vencer" ? undefined : "Vigente"}
                    </StatusBadge>
                  )}
                </div>

                {/* La celda ENTERA abre el modal —es el objetivo más grande y
                    la vigencia es el dato que más se corrige— y lleva el
                    `edit_calendar` visible al lado de la fecha: el tooltip solo
                    aparece recién después de que alguien decidió apuntar ahí,
                    así que no sirve para descubrir que la celda es editable.
                    `w-full` porque dentro de un flex en columna con
                    `items-start` el ancho del hijo es su contenido, y sin él
                    `truncate` no recorta y el texto se monta sobre las
                    acciones. */}
                <CeldaVigencia
                  label={tipo.label}
                  fechaVencimiento={documento.fecha_vencimiento}
                  avisos={documento.notificar_dias?.length ?? 0}
                  estado={vigencia}
                  onEditar={() => setVencimientoTarget(fila)}
                  ocupada={busyTipoId === tipo.id}
                  className="w-full"
                />

                <DocumentoFilaAcciones
                  label={tipo.label}
                  tieneDocumento
                  busy={busyTipoId === tipo.id}
                  uploading={uploading}
                  uploadPercent={uploadingPercents[tipo.id] ?? 0}
                  onPickFile={(file) => handlePickFile(tipo, file)}
                  onCancelUpload={() => handleCancelUpload(tipo.id)}
                  onDownload={() => handleDownload(tipo, documento)}
                  onRequestDelete={() => setDeleteTarget(fila)}
                />
              </div>
            );
          })}
        </section>
      )}

      <VigenciaModal
        open={vencimientoTarget !== null}
        onOpenChange={(open) => !open && setVencimientoTarget(null)}
        label={vencimientoTarget?.tipo?.label ?? ""}
        documento={vencimientoTarget?.documento ?? null}
        onSubmit={handleVencimientoSubmit}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>
                  Se eliminará <strong>{deleteTarget.documento.filename_original}</strong> de{" "}
                  <strong>{deleteTarget.tipo.label}</strong>. Esta acción no se puede deshacer.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
